/**
 * @fileoverview FeedbackPromptToast — one-time in-app feedback prompt
 *
 * Persistent bottom-right toast asking the user what tools or workflow changes
 * they'd like to see in Clockwise. Shown to every authenticated user until they
 * either submit feedback or click "No thanks" — both set
 * user.feedback_prompt_dismissed=true on the server so it doesn't reappear.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FiMessageSquare, FiX, FiSend, FiCheck } from 'react-icons/fi';
import '../styles/FeedbackPromptToast.css';

const MAX_LENGTH = 5000;

const FeedbackPromptToast = ({ apiRequest, onDismissed }) => {
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleDismiss = async (reason) => {
    // Optimistically hide the toast — re-showing on error would be more
    // annoying than letting one user slip past the prompt.
    onDismissed();
    try {
      await apiRequest('/user', {
        method: 'POST',
        body: JSON.stringify({ action: 'dismiss_feedback_prompt' })
      });
    } catch (e) {
      // best-effort; the prompt is hidden client-side either way
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) {
      setError('Please write a short message first.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await apiRequest('/feedback', {
        method: 'POST',
        body: JSON.stringify({
          message: trimmed,
          page_url: typeof window !== 'undefined' ? window.location.href : null
        })
      });
      if (response && response.ok === false) {
        // apiRequest may return a Response on non-ok
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not send feedback');
      }
      setSent(true);
      // Hide after a brief confirmation so the user sees the success state
      setTimeout(() => onDismissed(), 1800);
    } catch (err) {
      setError(err.message || 'Could not send feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="fbp-toast fbp-toast--sent" role="status" aria-live="polite">
        <FiCheck className="fbp-toast-success-icon" />
        <span>Thanks for the feedback!</span>
      </div>
    );
  }

  if (!expanded) {
    return (
      <div className="fbp-toast fbp-toast--collapsed" role="dialog" aria-label="Feedback prompt">
        <button
          type="button"
          className="fbp-toast-trigger"
          onClick={() => setExpanded(true)}
        >
          <FiMessageSquare className="fbp-toast-icon" />
          <div className="fbp-toast-trigger-text">
            <div className="fbp-toast-title">Got a minute?</div>
            <div className="fbp-toast-subtitle">Tell us what to build next.</div>
          </div>
        </button>
        <button
          type="button"
          className="fbp-toast-close"
          aria-label="Dismiss"
          onClick={() => handleDismiss('collapsed-close')}
        >
          <FiX />
        </button>
      </div>
    );
  }

  return (
    <div className="fbp-toast fbp-toast--expanded" role="dialog" aria-label="Feedback form">
      <div className="fbp-toast-header">
        <div className="fbp-toast-title">What would you like to see in Clockwise?</div>
        <button
          type="button"
          className="fbp-toast-close"
          aria-label="Close"
          onClick={() => setExpanded(false)}
        >
          <FiX />
        </button>
      </div>
      <p className="fbp-toast-help">
        Tools, shortcuts, reports, workflow changes — anything you'd want
        added or improved. We read every response.
      </p>
      <form onSubmit={handleSubmit}>
        <textarea
          className="fbp-toast-textarea"
          placeholder="e.g. A keyboard shortcut to start a timer on the last client I billed…"
          value={message}
          maxLength={MAX_LENGTH}
          onChange={(e) => { setMessage(e.target.value); if (error) setError(''); }}
          disabled={submitting}
          rows={4}
          autoFocus
        />
        <div className="fbp-toast-meta">
          <span className={message.length > MAX_LENGTH - 200 ? 'fbp-toast-count-warn' : ''}>
            {message.length} / {MAX_LENGTH}
          </span>
          {error && <span className="fbp-toast-error">{error}</span>}
        </div>
        <div className="fbp-toast-actions">
          <button
            type="button"
            className="fbp-toast-btn fbp-toast-btn--ghost"
            onClick={() => handleDismiss('no-thanks')}
            disabled={submitting}
          >
            No thanks
          </button>
          <button
            type="submit"
            className="fbp-toast-btn fbp-toast-btn--primary"
            disabled={submitting || !message.trim()}
          >
            <FiSend style={{ marginRight: 6 }} />
            {submitting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  );
};

FeedbackPromptToast.propTypes = {
  apiRequest: PropTypes.func.isRequired,
  onDismissed: PropTypes.func.isRequired,
};

export default FeedbackPromptToast;
